import type { DriverNames, TimestampInMillis } from '@cobliteam/common-types';
import moment from 'moment';
import { identificationTokenGraphQLV2ToIdentificationTokenV2 } from '../..';
import type { AddDriverGraphQLAPI, DriverAPI, DriverGraphQLAPI, SaveDriverAPIRequest, SaveDriverGraphQLAPI } from './APITypes';
import type { Driver, NewDriver } from './Types';

const dateTimeStrToMilli = (date: string | null): TimestampInMillis | undefined => {
  return (date && moment(date, 'YYYY-MM-DD HH:mm:ss ZZ').valueOf()) || undefined;
};

const dateStrToMilli = (date?: string): TimestampInMillis | undefined => {
  return (date && moment(date, 'YYYY-MM-DD').valueOf()) || undefined;
};

const millisToDateString = (millis?: TimestampInMillis): string | undefined => {
  return (millis && moment(millis).format('YYYY-MM-DD')) || undefined;
};

export const driverAPIToDriver = (driver: DriverAPI): Driver => {
  return {
    fleetId: driver.fleet_id,
    id: driver.id,
    name: driver.name,
    assigned: driver.assigned,
    phoneNumbers: driver.phone_numbers,
    firstLicenseAcquirementDateInMillis: dateTimeStrToMilli(driver.first_license_acquirement_date),
    licenseExpirationDateInMillis: dateTimeStrToMilli(driver.license_expiration_date),
    licenseCategory: driver.license_category || undefined,
    driverCode: driver.driver_code,
    identifier: driver.identifier || undefined,
    score: driver.score?.score ?? undefined,
  };
};

export const saveDriverParseToAPI = (driver: Driver | NewDriver): SaveDriverAPIRequest => {
  return {
    id: ('id' in driver && driver.id) || undefined,
    name: driver.name,
    phone_numbers: driver.phoneNumbers,
    license_category: driver.licenseCategory || null,
    driver_code: driver.driverCode || undefined,
    license_expiration_date: driver.licenseExpirationDateInMillis || null,
    first_license_acquirement_date: driver.firstLicenseAcquirementDateInMillis || null,
  };
};

export const driverGraphQLAPIToDriver = (graphQLDriver: DriverGraphQLAPI): Driver => {
  return {
    fleetId: graphQLDriver.fleet_id,
    id: graphQLDriver.id,
    name: graphQLDriver.name,
    assigned: graphQLDriver.assigned ?? undefined,
    phoneNumbers: [graphQLDriver.phone_number || ''],
    firstLicenseAcquirementDateInMillis: dateStrToMilli(graphQLDriver.first_license_acquirement_date),
    licenseExpirationDateInMillis: dateStrToMilli(graphQLDriver.license_expiration_date),
    licenseCategory: graphQLDriver.license_category || undefined,
    driverCode: graphQLDriver.driver_code ?? undefined,
    identifier: graphQLDriver.identifier?.toUpperCase() || undefined,
    identificationToken: graphQLDriver.identification_token ? identificationTokenGraphQLV2ToIdentificationTokenV2(graphQLDriver.identification_token) : undefined,
    cpf: graphQLDriver.cpf ?? undefined,
    driverLicense: graphQLDriver.driver_license ?? undefined,
    registryId: graphQLDriver.registry_id ?? undefined,
    profileImageUrl: graphQLDriver.profile_image_url || undefined,
    // Only `DriversWithVehicles` selects a vehicle, and only `id`/`licensePlate`/`device { id }` of
    // it — so this is deliberately narrower than the full domain `Vehicle`. The old hand-written
    // type claimed a whole `Vehicle` here, which the query never returned.
    vehicle: graphQLDriver.vehicle
      ? {
          id: graphQLDriver.vehicle.id,
          licensePlate: graphQLDriver.vehicle.licensePlate ?? undefined,
          deviceId: graphQLDriver.vehicle.device?.id,
        }
      : undefined,
  };
};

const cleanPhoneNumber = (string: string) => {
  return string.replace(/[^a-zA-Z0-9]/g, '');
};

export const newDriverToAddDriverGraphQLAPI = (driver: NewDriver): AddDriverGraphQLAPI => {
  const phone = driver.phoneNumbers?.length && driver.phoneNumbers[0] ? cleanPhoneNumber(driver.phoneNumbers[0]) : undefined;

  return {
    name: driver.name,
    phone_number: phone,
    first_license_acquirement_date: millisToDateString(driver.firstLicenseAcquirementDateInMillis),
    license_expiration_date: millisToDateString(driver.licenseExpirationDateInMillis),
    license_category: driver.licenseCategory,
    cpf: driver.cpf,
    driver_license: driver.driverLicense,
    registry_id: driver.registryId,
    // Only ever the write-side field: profileImageUrl holds a signed URL, and echoing it back would
    // persist an expiring signature.
    //
    // `remove_profile_image` deliberately does NOT belong here — `AddDriver` does not declare it
    // (a driver being created has no stored photo), so sending it made GraphQL reject the whole
    // mutation. Reachable before: add drawer -> pick a photo -> Remover -> Salvar.
    profile_image_base64: driver.profileImageBase64,
  };
};

export const driverToSaveDriverGraphQLAPI = (driver: Driver): SaveDriverGraphQLAPI => {
  return {
    id: driver.id,
    ...newDriverToAddDriverGraphQLAPI(driver),
    // Update-only: clearing a stored photo has no meaning on create.
    remove_profile_image: driver.removeProfileImage,
  };
};

export const driverGraphQLAPIToDriverNames = (graphQLDrivers: Array<Pick<DriverGraphQLAPI, 'id' | 'name'>>): DriverNames =>
  graphQLDrivers.reduce((acc, value) => {
    acc[value.id] = value.name;
    return acc;
  }, {} as DriverNames);
